import React, { useState, useEffect } from 'react';
import { Clock, Calendar, X } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL } from '../config/api';

export default function HiringCountdownBanner({ onDismiss }) {
  const [hiringStatus, setHiringStatus] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHiringStatus();
  }, []);

  useEffect(() => {
    if (hiringStatus?.countdown_date && !hiringStatus?.hiring_enabled) {
      const timer = setInterval(() => {
        const now = new Date().getTime();
        const target = new Date(hiringStatus.countdown_date).getTime();
        const diff = target - now;

        if (diff <= 0) {
          setTimeLeft(null);
          // Refresh status in case hiring has opened
          fetchHiringStatus();
        } else {
          const days = Math.floor(diff / (1000 * 60 * 60 * 24));
          const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((diff % (1000 * 60)) / 1000);
          setTimeLeft({ days, hours, minutes, seconds });
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [hiringStatus]);

  const fetchHiringStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/hiring/status`);
      setHiringStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch hiring status:', error);
    } finally {
      setLoading(false);
    }
  };

  // Don't show if loading, hiring is enabled, or no message
  if (loading || hiringStatus?.hiring_enabled || !hiringStatus) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-3 relative" data-testid="hiring-countdown-banner">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="bg-white/20 p-2 rounded-full">
              <Clock className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-lg">Hiring Opens Soon</p>
              <p className="text-white/90 text-sm">
                {hiringStatus.countdown_message || 'Browse available tradespeople while you wait.'}
              </p>
            </div>
          </div>

          {/* Countdown Timer */}
          {timeLeft && (
            <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
              <Calendar className="h-4 w-4" />
              <div className="flex items-center gap-3 text-sm font-mono">
                <div className="text-center">
                  <div className="text-xl font-bold">{timeLeft.days}</div>
                  <div className="text-xs text-white/70">days</div>
                </div>
                <span className="text-xl">:</span>
                <div className="text-center">
                  <div className="text-xl font-bold">{String(timeLeft.hours).padStart(2, '0')}</div>
                  <div className="text-xs text-white/70">hrs</div>
                </div>
                <span className="text-xl">:</span>
                <div className="text-center">
                  <div className="text-xl font-bold">{String(timeLeft.minutes).padStart(2, '0')}</div>
                  <div className="text-xs text-white/70">min</div>
                </div>
                <span className="text-xl">:</span>
                <div className="text-center">
                  <div className="text-xl font-bold">{String(timeLeft.seconds).padStart(2, '0')}</div>
                  <div className="text-xs text-white/70">sec</div>
                </div>
              </div>
            </div>
          )}

          {/* Non-countdown date display */}
          {!timeLeft && hiringStatus.countdown_date && (
            <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2 text-sm">
              <Calendar className="h-4 w-4" />
              <span>Opens {new Date(hiringStatus.countdown_date).toLocaleDateString('en-GB', {
                weekday: 'short',
                day: 'numeric',
                month: 'short'
              })}</span>
            </div>
          )}

          {onDismiss && (
            <button
              onClick={onDismiss}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Hook for checking hiring status
export function useHiringStatus() {
  const [hiringEnabled, setHiringEnabled] = useState(true);
  const [hiringStatus, setHiringStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/hiring/status`);
        setHiringStatus(response.data);
        setHiringEnabled(response.data.hiring_enabled);
      } catch (error) {
        console.error('Failed to fetch hiring status:', error);
        setHiringEnabled(true); // Default to enabled on error
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, []);

  return { hiringEnabled, hiringStatus, loading };
}
